import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createDraftPayload } from "@/config/campaignDirectorDraft.mjs";

const PATH = "/platform/campaign-director/drafts";
const DEFAULT_INSTRUCTION = "Create a Florida Final Expense campaign draft for adults requesting information. Include clear qualification and permission-based follow-up.";

function DraftContent({ draft }) {
  if (!draft) return (
    <p className="text-sm text-muted-foreground" data-testid="campaign-director-empty-detail">
      Select a saved draft or generate a new one to review it here.
    </p>
  );
  const p = draft.proposal || {};
  return (
    <div className="space-y-5" data-testid="campaign-director-draft">
      <div className="rounded-md border bg-muted/30 p-3 text-sm">
        <div className="font-semibold">{p.name || "Untitled draft"}</div>
        <p className="mt-1 text-muted-foreground">Draft only · Florida · Final Expense · Target: {draft.lead_target} leads</p>
        <p className="mt-1 text-muted-foreground">No advertising, consumer contact, assignment, or payment has been authorized.</p>
      </div>
      <div><h3 className="mb-1 text-sm font-semibold">Audience</h3><p className="whitespace-pre-wrap text-sm">{p.audience}</p></div>
      <div><h3 className="mb-1 text-sm font-semibold">Headline</h3><p className="text-sm">{p.headline}</p></div>
      <div><h3 className="mb-1 text-sm font-semibold">Draft copy</h3><p className="whitespace-pre-wrap text-sm">{p.body}</p></div>
      <div>
        <h3 className="mb-1 text-sm font-semibold">Qualification questions</h3>
        <ol className="list-decimal space-y-1 pl-5 text-sm">{(p.qualification_questions || []).map((item, i) => <li key={i}>{item}</li>)}</ol>
      </div>
      <div>
        <h3 className="mb-1 text-sm font-semibold">Proposed follow-up</h3>
        <ol className="list-decimal space-y-1 pl-5 text-sm">{(p.followup_outline || []).map((item, i) => <li key={i}>{item}</li>)}</ol>
      </div>
    </div>
  );
}

export default function CampaignDirector() {
  const [instruction, setInstruction] = useState(DEFAULT_INSTRUCTION);
  const [target, setTarget] = useState("10");
  const [drafts, setDrafts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api.get(PATH).then(({ data }) => {
      if (!active) return;
      const rows = Array.isArray(data) ? data : [];
      setDrafts(rows);
      setSelectedId(rows[0]?.id || "");
    }).catch((e) => {
      if (active) setError(formatApiErrorDetail(e?.response?.data?.detail) || "Unable to load drafts.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (generating) return;
    setError("");
    let payload;
    try {
      payload = createDraftPayload(instruction, target);
    } catch (e) {
      setError(e.message);
      return;
    }

    // This is the only action on this page that can call the paid AI provider.
    if (!window.confirm("Generate one AI campaign draft? This uses OpenAI API credits. No advertising or consumer calls will be started.")) return;

    setGenerating(true);
    try {
      const { data } = await api.post(PATH, payload);
      setDrafts((prev) => [data, ...prev]);
      setSelectedId(data.id);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Draft generation failed. Check the OpenAI connection.");
    } finally {
      setGenerating(false);
    }
  };

  const selected = drafts.find((item) => item.id === selectedId);

  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="campaign-director-page">
      <div>
        <h1 className="font-heading text-2xl font-bold">Campaign Director</h1>
        <p className="text-sm text-muted-foreground">
          Owner-only · Synthetic drafts · Florida Final Expense. This screen cannot launch advertisements, call leads, or sell lead inventory.
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <form onSubmit={submit} className="space-y-4 rounded-xl border bg-card p-5">
            <h2 className="font-semibold">Create a campaign draft</h2>
            <div className="space-y-2">
              <Label htmlFor="campaign-instruction">Instructions</Label>
              <textarea id="campaign-instruction" rows={6} maxLength={2000}
                value={instruction} onChange={(e) => setInstruction(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                data-testid="campaign-instruction" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-target">Lead target (1–100)</Label>
              <Input id="campaign-target" inputMode="numeric" value={target}
                onChange={(e) => setTarget(e.target.value)} data-testid="campaign-target" />
            </div>
            <p className="text-xs text-muted-foreground">Viewing saved drafts is free. Creating a new AI draft uses OpenAI API credits, but does not purchase ads or activate a campaign.</p>
            <Button type="submit" disabled={generating} data-testid="campaign-generate">
              {generating ? "Generating…" : "Generate AI draft (API credits)"}
            </Button>
            {error && <p className="text-sm text-destructive" role="alert" data-testid="campaign-error">{error}</p>}
          </form>

          <section className="rounded-xl border bg-card p-5">
            <h2 className="mb-3 font-semibold">Saved drafts</h2>
            {loading ? <p className="text-sm text-muted-foreground">Loading saved drafts…</p>
              : drafts.length === 0 ? <p className="text-sm text-muted-foreground">No drafts created yet.</p>
              : <div className="space-y-2">
                  {drafts.map((draft) => (
                    <button key={draft.id} type="button" onClick={() => setSelectedId(draft.id)}
                      className={`w-full rounded-md border p-3 text-left text-sm hover:bg-muted/40 ${selectedId === draft.id ? "border-primary" : ""}`}>
                      <span className="block font-medium">{draft.proposal?.name || "Untitled draft"}</span>
                      <span className="text-xs text-muted-foreground">{draft.state} · {draft.lead_target} target · Draft</span>
                    </button>
                  ))}
                </div>}
          </section>
        </div>
        <section className="rounded-xl border bg-card p-5">
          <h2 className="mb-4 font-semibold">Draft review</h2>
          <DraftContent draft={selected} />
        </section>
      </div>
    </div>
  );
}
